import { groupBy, nonNullishFilter, PublishedDocPageVisitsEntry } from "@supernova-studio/model";
import { DTOPublishedDocPageVisitData, DTOPublishedDocVisitData } from "../../dto";

//
// Analytics data conversion
//

export function documentationAnalyticsToGlobalDto(entries: PublishedDocPageVisitsEntry[]): DTOPublishedDocVisitData[] {
  return squashAndConvertToDto(entries);
}

//
// Analytics data per page conversion
//

export function documentationAnalyticsToPageDto(
  entries: PublishedDocPageVisitsEntry[]
): DTOPublishedDocPageVisitData[] {
  // Group by page id
  const byPageIdEntries = Array.from(groupBy(entries, e => e.pagePersistentId).entries());
  byPageIdEntries.sort((lhs, rhs) => lhs[0].localeCompare(rhs[0]));

  return byPageIdEntries.flatMap(([pagePersistentId, entries]) => {
    const dtoWithoutPageId = squashAndConvertToDto(entries);
    return dtoWithoutPageId.map(dto => ({ ...dto, pagePersistentId }));
  });
}

//
// Base
//

function squashAndConvertToDto(entries: PublishedDocPageVisitsEntry[]): DTOPublishedDocVisitData[] {
  // Group by beginning of day
  const byDay = groupBy(entries, e => getStartOfUTCDay(e.timestamp));

  const obj = Object.fromEntries(
    Array.from(byDay.entries()).map(([date, values]) => [new Date(date).toISOString(), values.length])
  );

  // Convert model to DTO by squashing model entries that belong to the same day
  return Array.from(byDay.entries()).map(([startOfDay, dayEntries]) => {
    const firstEntry = dayEntries[0];

    // Sessions are number of unique user IDs who visited the page during that day
    const uniqueSessions = new Set(dayEntries.map(e => e.anonymousId).filter(nonNullishFilter)).size;

    // Visits are sum of all recorded visits of the page during that day
    const visitsSum = dayEntries.reduce((prev, curr) => prev + curr.visits, 0);

    return {
      versionId: firstEntry.versionId,
      timestamp: new Date(startOfDay),
      sessions: uniqueSessions,
      visits: visitsSum,

      ...(firstEntry.locale && { locale: firstEntry.locale }),
    };
  });
}

function getStartOfUTCDay(date: Date): number {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  return Date.UTC(year, month, day, 0, 0, 0, 0);
}
